import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Trophy, CreditCard, Wallet, Tag, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ICONS = {
    PAYMENT_REQUEST: Trophy,
    PAYMENT_CONFIRMED: CreditCard,
    ITEM_SOLD: Tag,
    PAYOUT_PENDING: Wallet,
};

function timeAgo(iso) {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
}

export const NotificationBell = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/notifications");
            setItems(data.items || []);
            setUnread(data.unread_count || 0);
        } catch (e) {
            console.error("Failed to load notifications:", e);
        }
    }, []);

    useEffect(() => {
        if (!user) return;
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [user, load]);

    if (!user) return null;

    const openItem = async (n) => {
        try { await api.post(`/notifications/${n.id}/read`); } catch (e) { console.error("Failed to mark notification read:", e); }
        setOpen(false);
        load();
        if (n.link) navigate(n.link);
    };

    const markAll = async () => {
        try { await api.post("/notifications/read-all"); } catch (e) { console.error("Failed to mark all read:", e); }
        load();
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative rounded-[10px]" data-testid="notification-bell">
                    <Bell className="h-5 w-5" />
                    {unread > 0 && (
                        <span data-testid="notification-unread-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground">
                            {unread > 9 ? "9+" : unread}
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <span className="font-display text-sm font-semibold">Notifications</span>
                    {unread > 0 && (
                        <button onClick={markAll} data-testid="notification-mark-all" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                            <CheckCheck className="h-3.5 w-3.5" /> Mark all read
                        </button>
                    )}
                </div>
                <ScrollArea className="max-h-96">
                    {items.length === 0 ? (
                        <div className="px-4 py-10 text-center text-sm text-muted-foreground">No notifications yet</div>
                    ) : (
                        <ul className="divide-y divide-border">
                            {items.map((n) => {
                                const Icon = ICONS[n.type] || Bell;
                                return (
                                    <li key={n.id}>
                                        <button
                                            onClick={() => openItem(n)}
                                            data-testid="notification-item"
                                            className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-[background-color] duration-150 hover:bg-muted ${n.read ? "" : "bg-accent/40"}`}
                                        >
                                            <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                                                <Icon className="h-4 w-4" />
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="flex items-center justify-between gap-2">
                                                    <span className="truncate font-medium">{n.title}</span>
                                                    {!n.read && <span className="h-2 w-2 shrink-0 rounded-full bg-destructive" aria-label="unread" />}
                                                </span>
                                                <span className="mt-0.5 block text-xs text-muted-foreground line-clamp-2">{n.body}</span>
                                                <span className="mt-1 block text-[11px] text-muted-foreground/70">{timeAgo(n.created_at)}</span>
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
};
